import { supabase } from '@/lib/supabase';

// ════════════════════════════════════════════════════════════════
// «Мой месяц» — сон и настроение из ежедневных отметок (10.10.2026).
//
// Слава: «в моменте неясно, для чего я отмечаю и куда потом смотреть —
// давай из отметок строить графики по итогам месяца, чтобы видеть тренд».
// До этого отметка дня уходила только в разбор недели, а тот открывался
// с трёх отметок за неделю: большинство людей ставили сон и не получали ничего.
//
// Принцип тот же, что в weekInsights.ts: закономерности ищет КОД по заранее
// заданным гипотезам и порогам, а не модель. Формулировки — «похоже», не факт.
// Модели тут нет вовсе: экран открывается мгновенно и не стоит ничего.
// ════════════════════════════════════════════════════════════════

export type MoodDay = {
  date: string; // yyyy-MM-dd
  sleep: number | null;
  mood: number | null;
  tags: string[];
  note: string | null;
};

export type Insight = { id: string; emoji: string; text: string; basis: string };

export type MonthTrend = {
  days: MoodDay[];
  /** дней с любой отметкой */
  marked: number;
  /** сколько дней месяца уже прошло (для «12 из 18») */
  elapsed: number;
  sleepAvg: number | null;
  moodAvg: number | null;
  /** разница со средним прошлого месяца; null — сравнивать не с чем */
  sleepDelta: number | null;
  moodDelta: number | null;
  /** куда идёт линия внутри месяца: вторая половина отметок против первой */
  sleepDir: 'up' | 'down' | 'flat' | null;
  moodDir: 'up' | 'down' | 'flat' | null;
  heavyDays: number;
  badNights: number;
  insights: Insight[];
  dofiLine: string;
};

const MIN_GROUP = 2; // меньше двух дней в группе не сравниваем
const MIN_DIFF = 0.5; // полбалла по шкале 1–5 — меньше считаем шумом
const MIN_FOR_DIR = 4; // направление линии — от четырёх отметок

const avg = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null);
const r1 = (x: number) => Math.round(x * 10) / 10;
/** 4,3 — с запятой, как пишут по-русски */
const f1 = (x: number) => r1(x).toFixed(1).replace('.', ',');

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return many;
  if (m10 === 1) return one;
  if (m10 >= 2 && m10 <= 4) return few;
  return many;
}

function direction(xs: number[]): 'up' | 'down' | 'flat' | null {
  if (xs.length < MIN_FOR_DIR) return null;
  const half = Math.floor(xs.length / 2);
  const a = avg(xs.slice(0, half))!;
  const b = avg(xs.slice(xs.length - half))!;
  if (b - a >= MIN_DIFF) return 'up';
  if (a - b >= MIN_DIFF) return 'down';
  return 'flat';
}

const WEEKDAY = ['воскресенья', 'понедельники', 'вторники', 'среды', 'четверги', 'пятницы', 'субботы'];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parse(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export const SLEEP_LABEL = ['', 'Ночь мимо', 'Урывками', 'Средне', 'Выспался(лась)', 'Отлично'];
export const MOOD_LABEL = ['', 'Выжат(а)', 'Тяжело', 'Обычный', 'Хороший', 'Отличный'];
export const SLEEP_EMOJI = ['', '😵', '😖', '😐', '🙂', '😃'];
export const MOOD_EMOJI = ['', '😞', '😕', '😐', '🙂', '😃'];

/** Цвета клеток: от тёплого к изумрудному для дня, от глухого синего к светлому для сна */
export const MOOD_COLOR = ['', '#E2574C', '#E8A85C', '#7C8894', '#4FC58F', '#10E58E'];
export const SLEEP_COLOR = ['', '#3A4A78', '#4B63A8', '#5C86C9', '#55B4E6', '#3DD8FF'];

export function buildTrend(days: MoodDay[], prev: MoodDay[], year: number, month: number): MonthTrend {
  const sorted = [...days].sort((a, b) => (a.date < b.date ? -1 : 1));
  const sleeps = sorted.filter((d) => d.sleep != null).map((d) => d.sleep!);
  const moods = sorted.filter((d) => d.mood != null).map((d) => d.mood!);
  const sleepAvg = avg(sleeps);
  const moodAvg = avg(moods);
  // С прошлым месяцем сравниваем, только когда в обоих хватает отметок:
  // «+0,4» по двум точкам — шум, а выглядит как результат.
  const prevSleeps = prev.filter((d) => d.sleep != null).map((d) => d.sleep!);
  const prevMoods = prev.filter((d) => d.mood != null).map((d) => d.mood!);
  const prevSleep = sleeps.length >= MIN_FOR_DIR && prevSleeps.length >= MIN_FOR_DIR ? avg(prevSleeps) : null;
  const prevMood = moods.length >= MIN_FOR_DIR && prevMoods.length >= MIN_FOR_DIR ? avg(prevMoods) : null;

  const today = new Date();
  const lastDay = new Date(year, month + 1, 0).getDate();
  const isCurrent = today.getFullYear() === year && today.getMonth() === month;
  const elapsed = isCurrent ? today.getDate() : lastDay;

  const insights: Insight[] = [];

  // 1. Сон → день: в те же сутки, сон отмечают про прошедшую ночь
  const afterGood = sorted.filter((d) => d.sleep != null && d.sleep >= 4 && d.mood != null).map((d) => d.mood!);
  const afterBad = sorted.filter((d) => d.sleep != null && d.sleep <= 2 && d.mood != null).map((d) => d.mood!);
  if (afterGood.length >= MIN_GROUP && afterBad.length >= MIN_GROUP) {
    const g = avg(afterGood)!, b = avg(afterBad)!;
    if (g - b >= MIN_DIFF) {
      insights.push({
        id: 'sleep_mood',
        emoji: '🌙',
        text: `Похоже, сон тянет за собой день: после хорошей ночи день в среднем ${f1(g)} из 5, после плохой — ${f1(b)}.`,
        basis: `${afterGood.length} ${plural(afterGood.length, 'день', 'дня', 'дней')} против ${afterBad.length}`,
      });
    }
  }

  // 2. Теги: какой тянет день вниз, какой поднимает
  if (moodAvg != null) {
    const byTag = new Map<string, number[]>();
    for (const d of sorted) {
      if (d.mood == null) continue;
      for (const t of d.tags ?? []) byTag.set(t, [...(byTag.get(t) ?? []), d.mood]);
    }
    let worst: { tag: string; a: number; n: number } | null = null;
    let best: { tag: string; a: number; n: number } | null = null;
    for (const [tag, xs] of byTag) {
      if (xs.length < MIN_GROUP) continue;
      const a = avg(xs)!;
      if (moodAvg - a >= MIN_DIFF && (!worst || a < worst.a)) worst = { tag, a, n: xs.length };
      if (a - moodAvg >= MIN_DIFF && (!best || a > best.a)) best = { tag, a, n: xs.length };
    }
    if (worst) {
      insights.push({
        id: 'tag_down',
        emoji: '📉',
        text: `Дни с отметкой «${worst.tag}» проходят тяжелее: ${f1(worst.a)} против ${f1(moodAvg)} в среднем.`,
        basis: `${worst.n} ${plural(worst.n, 'день', 'дня', 'дней')} с этой отметкой`,
      });
    }
    if (best) {
      insights.push({
        id: 'tag_up',
        emoji: '📈',
        text: `А «${best.tag}» дни поднимает: ${f1(best.a)} против ${f1(moodAvg)} в среднем.`,
        basis: `${best.n} ${plural(best.n, 'день', 'дня', 'дней')} с этой отметкой`,
      });
    }
  }

  // 3. День недели — только если есть явный лидер и явный аутсайдер
  const byDow = new Map<number, number[]>();
  for (const d of sorted) {
    if (d.mood == null) continue;
    const k = parse(d.date).getDay();
    byDow.set(k, [...(byDow.get(k) ?? []), d.mood]);
  }
  const dows = [...byDow.entries()].filter(([, xs]) => xs.length >= MIN_GROUP).map(([k, xs]) => ({ k, a: avg(xs)!, n: xs.length }));
  if (dows.length >= 2) {
    const top = dows.reduce((a, b) => (b.a > a.a ? b : a));
    const low = dows.reduce((a, b) => (b.a < a.a ? b : a));
    if (top.a - low.a >= 1) {
      insights.push({
        id: 'weekday',
        emoji: '🗓',
        text: `Лучше всего у тебя ${WEEKDAY[top.k]} (${f1(top.a)}), тяжелее — ${WEEKDAY[low.k]} (${f1(low.a)}).`,
        basis: `по ${Math.min(top.n, low.n)}+ отметкам на день недели`,
      });
    }
  }

  const heavyDays = moods.filter((m) => m <= 2).length;
  const badNights = sleeps.filter((s) => s <= 2).length;
  const moodDir = direction(moods);
  const sleepDir = direction(sleeps);

  // Фраза Дофи — по самому заметному. Анти-стыд: ни одного укора.
  let dofiLine: string;
  const marked = sorted.filter((d) => d.sleep != null || d.mood != null).length;
  if (marked === 0) {
    dofiLine = 'Пока пусто — и это нормально. Отметь сегодня, и на графике появится первая точка.';
  } else if (marked < 4) {
    dofiLine = `Уже ${marked} ${plural(marked, 'отметка', 'отметки', 'отметок')}. С четырёх я начну видеть, куда идёт линия.`;
  } else if (moodDir === 'up') {
    dofiLine = 'Линия дня идёт вверх. Что бы ты ни делал(а) иначе — оно работает.';
  } else if (sleepDir === 'up') {
    dofiLine = 'Сон выправляется. Обычно через неделю-другую за ним подтягиваются и дни.';
  } else if (heavyDays >= 3) {
    dofiLine = `${heavyDays} ${plural(heavyDays, 'тяжёлый день', 'тяжёлых дня', 'тяжёлых дней')} за месяц — это много сил. Ты это видишь, а значит, уже не один(на) с этим.`;
  } else if (moodDir === 'down' || sleepDir === 'down') {
    dofiLine = 'Линия пошла вниз — это не оценка тебе, а сигнал: телу нужна пауза.';
  } else {
    dofiLine = 'Месяц идёт ровно. Ровно — это тоже результат: тело любит предсказуемость.';
  }

  return {
    days: sorted,
    marked,
    elapsed,
    sleepAvg: sleepAvg == null ? null : r1(sleepAvg),
    moodAvg: moodAvg == null ? null : r1(moodAvg),
    sleepDelta: sleepAvg != null && prevSleep != null ? r1(sleepAvg - prevSleep) : null,
    moodDelta: moodAvg != null && prevMood != null ? r1(moodAvg - prevMood) : null,
    sleepDir,
    moodDir,
    heavyDays,
    badNights,
    insights,
    dofiLine,
  };
}

/** Отметки за месяц и за предыдущий (для сравнения). month — 0…11. */
export async function loadMonthTrend(userId: string, year: number, month: number): Promise<MonthTrend> {
  const from = ymd(new Date(year, month - 1, 1));
  const to = ymd(new Date(year, month + 1, 0));
  const start = ymd(new Date(year, month, 1));
  const { data, error } = await supabase
    .from('daily_checkins')
    .select('date, sleep, mood, tags, note')
    .eq('user_id', userId)
    .gte('date', from)
    .lte('date', to)
    .order('date', { ascending: true });
  if (error) console.error('[moodTrend] load failed', error);
  const rows = (data ?? []) as MoodDay[];
  return buildTrend(rows.filter((d) => d.date >= start), rows.filter((d) => d.date < start), year, month);
}

/** Самая ранняя отметка — чтобы не листать в пустые месяцы */
export async function firstCheckinDate(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from('daily_checkins')
    .select('date')
    .eq('user_id', userId)
    .order('date', { ascending: true })
    .limit(1)
    .maybeSingle();
  return (data?.date as string) ?? null;
}

/** Человек сам включил отслеживание тренда — хранится в профиле, на сервере */
export async function enableTrend(userId: string): Promise<string | null> {
  const at = new Date().toISOString();
  const { error } = await supabase.from('profiles').update({ trend_tracking_at: at }).eq('id', userId);
  if (error) {
    console.error('[moodTrend] enable failed', error);
    return null;
  }
  return at;
}
